import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../redux/redux.hooks';
import {
  fetchAdvancedSettings,
  postAdvancedSetting,
  updateWidgetFeatures,
} from '../reducers/advanced.settings.reducer';
import { useParams } from 'react-router-dom';
import { getAvailableFeatures } from '../reducers/available.features.reducer';
import { Tooltip } from 'antd';
import { MdInfoOutline } from 'react-icons/md';
import Loading from '../components/loading/Loading';
import { useToast } from '../components/toast/ToastProvider';
import { P } from '../components/font/Parargraph';
import { Editor } from '@monaco-editor/react';
import { AvailableFeaturesModel } from '../models/available.features';
import {
  getValueByPath,
  updateValueByPath,
} from '../utils/object.value.helper';

type SectionKey = 'boolean' | 'textfield' | 'json';

type FeatureGroup = {
  group: string;
  features: AvailableFeaturesModel[];
};

type FeatureSection = {
  key: SectionKey;
  title: string;
  groups: FeatureGroup[];
};

const SECTION_TITLES: Record<SectionKey, string> = {
  boolean: 'Toggles',
  textfield: 'Text values',
  json: 'Lists / JSON',
};

// Shown in their own card above every section, whether or not the
// available_features table has rows for them.
export const BILLING_TOGGLE: AvailableFeaturesModel = {
  id: 'billing-v2',
  feature_group: 'features',
  feature: 'billing_v2',
  feature_type: 'boolean',
  default_value: 'false',
  feature_description:
    'Alternative billing: shows the shop the new billing page ($30/month base subscription with add-ons) instead of the legacy plans. Existing subscriptions are not migrated.',
};

// features.billing_v2_annual is an object; the card edits its price and
// orders. Other keys (addon_discount_percent, addon_prices) are kept as is.
const ANNUAL_KEY = 'billing_v2_annual';
type AnnualField = 'price' | 'orders';

const isBillingFeature = ({ feature_group, feature }: AvailableFeaturesModel) =>
  (feature_group === 'features' &&
    (feature === BILLING_TOGGLE.feature || feature === ANNUAL_KEY)) ||
  feature_group === `features.${ANNUAL_KEY}`;

const isPlainObject = (value: any) =>
  !!value && typeof value === 'object' && !Array.isArray(value);

// Sets one annual field, dropping it when cleared, and removes the whole
// object once it is empty so annual billing turns off.
export const updateAnnualBilling = (
  root: any,
  field: AnnualField,
  text: string
) => {
  const parsedRoot = root
    ? typeof root === 'object' ? root : JSON.parse(root)
    : {};
  const features = isPlainObject(parsedRoot.features)
    ? { ...parsedRoot.features }
    : {};
  const annual = isPlainObject(features[ANNUAL_KEY])
    ? { ...features[ANNUAL_KEY] }
    : {};
  const trimmed = text.trim();
  if (trimmed === '') {
    delete annual[field];
  } else if (field === 'orders' && /^\d+$/.test(trimmed)) {
    annual[field] = Number(trimmed);
  } else {
    annual[field] = trimmed;
  }
  if (Object.keys(annual).length === 0) {
    delete features[ANNUAL_KEY];
  } else {
    features[ANNUAL_KEY] = annual;
  }
  return { ...parsedRoot, features };
};

const AnnualBillingField = ({
  field,
  label,
  details,
  value,
  onCommit,
}: {
  field: AnnualField;
  label: string;
  details: string;
  value: any;
  onCommit: (field: AnnualField, text: string) => void;
}) => {
  const [draft, setDraft] = useState(() => textDisplayValue(value));
  const committed = useRef(value);

  useEffect(() => {
    if (!Object.is(value, committed.current)) {
      committed.current = value;
      setDraft(textDisplayValue(value));
    }
  }, [value]);

  return (
    <s-text-field
      label={label}
      details={details}
      value={draft}
      autocomplete='off'
      onInput={(event) => {
        const text = event.currentTarget.value;
        setDraft(text);
        const trimmed = text.trim();
        committed.current =
          trimmed === ''
            ? undefined
            : field === 'orders' && /^\d+$/.test(trimmed)
              ? Number(trimmed)
              : trimmed;
        onCommit(field, text);
      }}
    />
  );
};

const featureSectionKey = ({ feature_type }: AvailableFeaturesModel): SectionKey =>
  feature_type === 'boolean' || feature_type === 'textfield'
    ? feature_type
    : 'json';

// Splits features by type (unknown types fall back to JSON), then by
// feature_group, keeping the API order inside each group. Billing features
// are left out because they have their own card.
export const groupFeatures = (
  features: AvailableFeaturesModel[]
): FeatureSection[] => {
  const sections: Record<SectionKey, FeatureGroup[]> = {
    boolean: [],
    textfield: [],
    json: [],
  };
  features.filter((feature) => !isBillingFeature(feature)).forEach((feature) => {
    const key = featureSectionKey(feature);
    let group = sections[key].find((g) => g.group === feature.feature_group);
    if (!group) {
      group = { group: feature.feature_group, features: [] };
      sections[key].push(group);
    }
    group.features.push(feature);
  });
  return (Object.keys(sections) as SectionKey[])
    .filter((key) => sections[key].length > 0)
    .map((key) => ({ key, title: SECTION_TITLES[key], groups: sections[key] }));
};

const parseInputValue = (value: string) => {
  try {
    return JSON.parse(value);
  } catch (err) {
    return value;
  }
};

const textDisplayValue = (value: any) => {
  if (value === undefined || value === null) {
    return '';
  }
  return typeof value === 'string' ? value : JSON.stringify(value);
};

const jsonDisplayValue = (value: any) => (value ? JSON.stringify(value) : '');

// Keeps the typed text locally so JSON parsing doesn't fight the input, and
// only resyncs when the stored value changes from outside this field.
const useDraft = (
  value: any,
  toDisplay: (value: any) => string,
  onCommit: (value: any) => void
): [string, (text: string) => void] => {
  const [draft, setDraft] = useState(() => toDisplay(value));
  const committed = useRef(value);

  useEffect(() => {
    if (!Object.is(value, committed.current)) {
      committed.current = value;
      setDraft(toDisplay(value));
    }
  }, [value, toDisplay]);

  const onChange = (text: string) => {
    const parsed = parseInputValue(text);
    committed.current = parsed;
    setDraft(text);
    onCommit(parsed);
  };

  return [draft, onChange];
};

const FeatureInfo = ({ feature }: { feature: AvailableFeaturesModel }) => {
  const { feature: name, feature_description, default_value } = feature;
  const title = (
    <>
      {feature_description && <div>{feature_description}</div>}
      <div>Default: {default_value || '(empty)'}</div>
    </>
  );
  return (
    <Tooltip
      title={title}
      placement='top'
      trigger={['hover', 'focus']}
      styles={{ root: { pointerEvents: 'none' } }}
    >
      <InfoButton type='button' aria-label={`About ${name}`}>
        <MdInfoOutline aria-hidden='true' />
      </InfoButton>
    </Tooltip>
  );
};

const TextFeature = ({
  feature,
  value,
  onCommit,
}: {
  feature: AvailableFeaturesModel;
  value: any;
  onCommit: (value: any) => void;
}) => {
  const [draft, onChange] = useDraft(value, textDisplayValue, onCommit);
  return (
    <TextRow>
      <FeatureName>{feature.feature}</FeatureName>
      <s-text-field
        label={feature.feature}
        labelAccessibilityVisibility='exclusive'
        value={draft}
        onInput={(event) => onChange(event.currentTarget.value)}
        autocomplete='off'
      />
      <RowMeta>
        {feature.default_value && (
          <DefaultValue>default: {feature.default_value}</DefaultValue>
        )}
        <FeatureInfo feature={feature} />
      </RowMeta>
    </TextRow>
  );
};

const JsonFeature = ({
  feature,
  value,
  onCommit,
}: {
  feature: AvailableFeaturesModel;
  value: any;
  onCommit: (value: any) => void;
}) => {
  const [draft, onChange] = useDraft(value, jsonDisplayValue, onCommit);
  return (
    <JsonRow>
      <JsonHeader>
        <FeatureName>{feature.feature}</FeatureName>
        {feature.default_value && (
          <DefaultValue>default: {feature.default_value}</DefaultValue>
        )}
        <FeatureInfo feature={feature} />
      </JsonHeader>
      <EditorContainer>
        <Editor
          value={draft}
          defaultValue=''
          height={'100%'}
          width={'100%'}
          language={'json'}
          wrapperProps={{ 'aria-label': `${feature.feature} value` }}
          options={{
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            lineNumbers: 'off',
            wordWrap: 'on',
          }}
          onChange={(v) => onChange(v ?? '')}
        />
      </EditorContainer>
    </JsonRow>
  );
};

const AvailableFeatures = () => {
  const params = useParams();
  const { id: shopId } = params;
  const dispatch = useAppDispatch();
  const { data: availableFeaturesData, status: availableFeaturesStatus } =
    useAppSelector((state) => state.availableFeaturesReducer);
  const {
    data: advancedSettingsData,
    status: advancedSettingsStatus,
    postWidgetFeaturesStatus,
  } = useAppSelector((state) => state.advancedSettingsReducer);

  const [widgetFeatures, setWidgetFeatures] = useState<any>('');
  // Latest value, updated synchronously so quick successive edits and an
  // immediate submit never work from a stale copy.
  const widgetFeaturesRef = useRef<any>('');
  const toast = useToast();
  const [updatingFeatures, setUpdatingFeatures] = useState(false)

  const sections = useMemo(
    () => groupFeatures(availableFeaturesData),
    [availableFeaturesData]
  );

  const replaceWidgetFeatures = (value: any) => {
    widgetFeaturesRef.current = value;
    setWidgetFeatures(value);
  };

  const commitFeatureValue = (
    value: any,
    feature_group: string,
    feature: string
  ) => {
    const updated = updateValueByPath(
      widgetFeaturesRef.current,
      value,
      feature_group,
      feature
    );
    replaceWidgetFeatures(updated);
    return updated;
  };

  const handleSubmit = () => {
    const current = widgetFeaturesRef.current;
    if (!current) {
      return;
    }
    dispatch(
      postAdvancedSetting({
        value:
          typeof current === 'object'
            ? JSON.stringify(current)
            : current,
        shopId: shopId!,
        settingType: 'widgetfeatures',
      })
    );
    setUpdatingFeatures(true)
  };

  useEffect(() => {
    dispatch(fetchAdvancedSettings(shopId!));
    dispatch(getAvailableFeatures());
  }, []);

  useEffect(() => {
    if (postWidgetFeaturesStatus === 'successful' && updatingFeatures) {
      toast.show('Successfully updated the features')
      setUpdatingFeatures(false)
    } else if (postWidgetFeaturesStatus === 'error' && updatingFeatures) {
      toast.show('Failed to update the features', { isError: true })
      setUpdatingFeatures(false)
    }
  }, [postWidgetFeaturesStatus])

  useEffect(() => {
    if (advancedSettingsStatus === 'successful' && advancedSettingsData) {
      replaceWidgetFeatures(advancedSettingsData.widgetFeatures);
    }
  }, [advancedSettingsStatus]);

  const renderFeature = (key: SectionKey, feature: AvailableFeaturesModel) => {
    const { id, feature_group, feature: name } = feature;
    const value = getValueByPath(widgetFeatures, feature_group, name);
    switch (key) {
      case 'boolean':
        return (
          <ToggleItem key={`id-${id}`}>
            <s-checkbox
              name={name}
              checked={!!value}
              label={name}
              onChange={(event) => {
                const updated = commitFeatureValue(event.currentTarget.checked, feature_group, name);
                dispatch(updateWidgetFeatures(updated));
              }}
            />
            <FeatureInfo feature={feature} />
          </ToggleItem>
        );
      case 'textfield':
        return (
          <TextFeature
            key={`id-${id}`}
            feature={feature}
            value={value}
            onCommit={(v) => commitFeatureValue(v, feature_group, name)}
          />
        );
      default:
        return (
          <JsonFeature
            key={`id-${id}`}
            feature={feature}
            value={value}
            onCommit={(v) => commitFeatureValue(v, feature_group, name)}
          />
        );
    }
  };

  const commitAnnualBilling = (field: AnnualField, text: string) => {
    replaceWidgetFeatures(
      updateAnnualBilling(widgetFeaturesRef.current, field, text)
    );
  };

  const renderBilling = () => {
    const annual = getValueByPath(widgetFeatures, 'features', ANNUAL_KEY);
    const annualValue = (field: AnnualField) =>
      isPlainObject(annual) ? annual[field] : undefined;
    return (
      <Card aria-labelledby='features-section-billing'>
        <SectionTitle id='features-section-billing'>Alternative billing</SectionTitle>
        <FeatureList>
          {renderFeature('boolean', BILLING_TOGGLE)}
          <GroupTitle>Annual billing</GroupTitle>
          <AnnualFields>
            <AnnualBillingField
              field='price'
              label='Annual price (USD)'
              details='Base price per year, e.g. 300.00'
              value={annualValue('price')}
              onCommit={commitAnnualBilling}
            />
            <AnnualBillingField
              field='orders'
              label='Annual orders'
              details='Shown in the plan name, e.g. 10000'
              value={annualValue('orders')}
              onCommit={commitAnnualBilling}
            />
          </AnnualFields>
          <DefaultValue>
            Needs alternative billing on. Leave both empty to turn annual
            billing off; if only one is set, the shop sees "Annual billing
            unavailable".
          </DefaultValue>
        </FeatureList>
      </Card>
    );
  };

  const renderSections = () =>
    sections.map(({ key, title, groups }) => (
      <Card key={key} aria-labelledby={`features-section-${key}`}>
        <SectionTitle id={`features-section-${key}`}>{title}</SectionTitle>
        {groups.map(({ group, features }) => (
          <GroupRow key={group} $stacked={key !== 'boolean'}>
            <GroupTitle>{group}</GroupTitle>
            {key === 'boolean' ? (
              <ToggleGrid>
                {features.map((feature) => renderFeature(key, feature))}
              </ToggleGrid>
            ) : (
              <FeatureList>
                {features.map((feature) => renderFeature(key, feature))}
              </FeatureList>
            )}
          </GroupRow>
        ))}
      </Card>
    ));

  const renderView = () => {
    if (
      availableFeaturesStatus === 'loading' ||
      advancedSettingsStatus === 'loading'
    ) {
      return (
        <Loading loadingMessage='Loading the available Easysize features' />
      );
    } else if (
      availableFeaturesStatus === 'successful' &&
      advancedSettingsStatus === 'successful'
    ) {
      return (
        <Container>
          <InnerContainer>
            {renderBilling()}
            {renderSections()}
          </InnerContainer>
        </Container>
      );
    }
    return null;
  };

  return (
    <>
      {renderView()}
      <UpdateFeatures onClick={handleSubmit}>
        {postWidgetFeaturesStatus === 'loading' ? (
          <s-spinner accessibilityLabel='Updating features' />
        ) : (
          <P>Update features</P>
        )}
      </UpdateFeatures>
    </>
  );
};

export default AvailableFeatures;

const Container = styled.div`
  width: 100%;
  box-sizing: border-box;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 16px 24px 112px;
  align-items: center;
  justify-content: flex-start;
  @media (max-width: 1023px) { padding: 10px 12px 112px; }
`;

const InnerContainer = styled.div`
  width: 100%;
  max-width: 1160px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Card = styled.section`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  background: white;
  box-shadow: 4px 4px 12px #ccccccb3;
  padding: 16px 20px;
  border-radius: 12px;
`;

const SectionTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
`;

const GroupRow = styled.div<{ $stacked: boolean }>`
  display: grid;
  grid-template-columns: ${({ $stacked }) =>
    $stacked ? 'minmax(0, 1fr)' : 'minmax(140px, 220px) minmax(0, 1fr)'};
  gap: ${({ $stacked }) => ($stacked ? '4px' : '8px 16px')};
  align-items: start;
  padding: 10px 0;
  border-top: 1px solid #eceeef;
  &:first-of-type { border-top: none; }
  @media (max-width: 1023px) { grid-template-columns: minmax(0, 1fr); }
`;

const GroupTitle = styled.h3`
  margin: 0;
  padding-top: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #6d7175;
  overflow-wrap: anywhere;
`;

const ToggleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 0 16px;
  min-width: 0;
`;

const ToggleItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const TextRow = styled.div`
  display: grid;
  grid-template-columns: minmax(160px, 260px) minmax(0, 1fr) minmax(96px, 160px);
  gap: 12px;
  align-items: center;
  @media (max-width: 1023px) { grid-template-columns: minmax(0, 1fr); gap: 4px; }
`;

const JsonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const JsonHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const FeatureName = styled.span`
  font-size: 14px;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const RowMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  min-width: 0;
`;

const DefaultValue = styled.span`
  font-size: 12px;
  color: #6d7175;
  overflow-wrap: anywhere;
`;

const InfoButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #6d7175;
  font-size: 16px;
  cursor: help;
  &:hover { color: #202223; }
  &:focus-visible { outline: 2px solid #2c6ecb; outline-offset: 1px; }
`;

const AnnualFields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 260px));
  gap: 12px;
  @media (max-width: 1023px) { grid-template-columns: minmax(0, 1fr); }
`;

const EditorContainer = styled.div`
  height: 120px;
  width: 100%;
  min-width: 0;
  border: 1px solid #c9cccf;
  border-radius: 4px;
  overflow: hidden;
`;

const UpdateFeatures = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  padding-top: 12px;
  padding-left: 24px;
  padding-right: 24px;
  border-radius: 36px;
  bottom: 48px;
  right: 48px;
  color: #315f46;
  background-color: white;
  text-align: center;
  box-shadow: 4px 4px 10px #939794cc;
  cursor: pointer;
  z-index: 100;
`;
